import { Box, Flex, Text } from "@chakra-ui/react";
import type { MapRef } from "react-map-gl/maplibre";

import { Swatch } from "../features/swatch";
import type {
  CompositeLegendEntry,
  InterpolateLegendEntry,
  LegendEntry,
  MatchLegendEntry,
  SpriteLegendEntry,
  StaticLegendEntry,
  StepLegendEntry,
} from "../types/legend-types";

export interface LegendRowProps {
  entry: LegendEntry;
  map: MapRef | undefined;
}

export function LegendRow({ entry, map }: LegendRowProps) {
  if (entry.kind === "static") return <StaticRow entry={entry} />;
  if (entry.kind === "match") return <MatchRows entry={entry} />;
  if (entry.kind === "step") return <StepRows entry={entry} />;
  if (entry.kind === "interpolate") return <InterpolateBar entry={entry} />;
  if (entry.kind === "composite-fill" || entry.kind === "composite-line")
    return <CompositeRow entry={entry} map={map} />;
  if (entry.kind === "sprite") return <SpriteRows entry={entry} map={map} />;
  return null;
}

// ---------------------------------------------------------------------------
// Sub-row renderers
// ---------------------------------------------------------------------------

function StaticRow({ entry }: { entry: StaticLegendEntry }) {
  return (
    <Flex align="center" gap={2}>
      <Swatch>
        <Swatch.Simple
          color={entry.color}
          swatchType={entry.swatchType}
          borderColor={entry.borderColor}
          borderWidth={entry.borderWidth}
        />
      </Swatch>
      {entry.layerLabel && <Text fontSize="xs">{entry.layerLabel}</Text>}
    </Flex>
  );
}

function MatchRows({ entry }: { entry: MatchLegendEntry }) {
  return (
    <Flex direction="column" gap={1}>
      {entry.values.map(({ label, color }) => (
        <Flex key={label} align="center" gap={2}>
          <Swatch>
            <Swatch.Simple color={color} swatchType={entry.swatchType} />
          </Swatch>
          {label && <Text fontSize="xs">{label}</Text>}
        </Flex>
      ))}
    </Flex>
  );
}

function StepRows({ entry }: { entry: StepLegendEntry }) {
  return (
    <Flex direction="column" gap={1}>
      {entry.steps.map(({ label, color }) => (
        <Flex key={label} align="center" gap={2}>
          <Swatch>
            <Swatch.Simple color={color} swatchType={entry.swatchType} />
          </Swatch>
          {label && <Text fontSize="xs">{label}</Text>}
        </Flex>
      ))}
    </Flex>
  );
}

// Look at CompositeLegendEntry for an explanation of this one!
function CompositeRow({ entry, map }: { entry: CompositeLegendEntry; map: MapRef | undefined }) {
  return (
    <Flex align="center" gap={2}>
      <Swatch>
        <Swatch.Composite entry={entry} map={map} />
      </Swatch>
      {entry.layerLabel && <Text fontSize="xs">{entry.layerLabel}</Text>}
    </Flex>
  );
}

function SpriteRows({ entry, map }: { entry: SpriteLegendEntry; map: MapRef | undefined }) {
  return (
    <Flex direction="column" gap={1}>
      {entry.icons.map(({ label, iconName, rotation }) => (
        <Flex key={iconName + label} align="center" gap={2}>
          <Swatch>
            <Swatch.Sprite iconName={iconName} map={map} rotation={rotation} />
          </Swatch>
          {label && <Text fontSize="xs">{label}</Text>}
        </Flex>
      ))}
    </Flex>
  );
}

function InterpolateBar({ entry }: { entry: InterpolateLegendEntry }) {
  if (entry.stops.length === 0) return null;

  const gradientStops = entry.stops
    .map(({ color }, i) => {
      const pct = entry.stops.length > 1 ? Math.round((i / (entry.stops.length - 1)) * 100) : 0;
      return `${color} ${pct}%`;
    })
    .join(", ");

  return (
    <Flex direction="column" gap={1}>
      <Box
        h={4}
        w="full"
        borderRadius="base"
        style={{ background: `linear-gradient(to right, ${gradientStops})` }}
      />
      <Flex justify="space-between">
        <Text fontSize="xs">{entry.stops[0].stop}</Text>
        <Text fontSize="xs">{entry.stops[entry.stops.length - 1].stop}</Text>
      </Flex>
    </Flex>
  );
}
